/**
 * Display Formatter
 *
 * Formats values for display using the Intl APIs based on display_format metadata.
 * Uses browser locale for number formatting but explicit currency codes.
 */

/**
 * Convert a string to title case.
 */
function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
}

/**
 * Get date format options based on format argument.
 */
function getDateOptions(formatArg?: string): Intl.DateTimeFormatOptions {
  switch (formatArg) {
    case 'short':
      return { dateStyle: 'short' }
    case 'long':
      return { dateStyle: 'long' }
    default:
      return { dateStyle: 'medium' }
  }
}

/**
 * Get datetime format options based on format argument.
 */
function getDateTimeOptions(formatArg?: string): Intl.DateTimeFormatOptions {
  switch (formatArg) {
    case 'short':
      return { dateStyle: 'short', timeStyle: 'short' }
    default:
      return { dateStyle: 'medium', timeStyle: 'short' }
  }
}

/**
 * Format a US phone number (10 digits) as (XXX) XXX-XXXX.
 * Returns raw value if not 10 digits.
 */
function formatUSPhone(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  }
  return value
}

/**
 * Format a value for display based on the display_format metadata field.
 *
 * @param value - The value to format
 * @param format - The display_format string (e.g., 'currency', 'number:2', 'percent', 'date:short')
 * @param locale - The locale for number formatting (defaults to browser locale)
 * @returns The formatted string
 *
 * Supported formats:
 * - currency - Currency with USD default (e.g., '$1,234.56')
 * - currency:EUR - Currency with explicit code
 * - number - Locale-aware number
 * - number:2 - Number with 2 decimal places
 * - percent - Percentage (value * 100)
 * - percent:2 - Percentage with 2 decimal places
 * - date - Date only
 * - date:short - Short date format
 * - date:long - Long date format
 * - datetime - Date and time
 * - datetime:short - Short datetime format
 * - phone - US phone format (XXX) XXX-XXXX
 * - text:upper - Uppercase
 * - text:lower - Lowercase
 * - text:title - Title Case
 */
export function formatValue(
  value: unknown,
  format: string,
  locale: string = navigator.language
): string {
  // Handle null/undefined
  if (value == null) {
    return ''
  }

  // Parse format string
  const colonIndex = format.indexOf(':')
  const formatType = colonIndex === -1 ? format : format.substring(0, colonIndex)
  const formatArg = colonIndex === -1 ? undefined : format.substring(colonIndex + 1)

  switch (formatType) {
    case 'currency': {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value))
      if (isNaN(numValue)) return String(value)

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: formatArg || 'USD',
      }).format(numValue)
    }

    case 'number': {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value))
      if (isNaN(numValue)) return String(value)

      const decimals = formatArg ? parseInt(formatArg, 10) : 0
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue)
    }

    case 'percent': {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value))
      if (isNaN(numValue)) return String(value)

      const decimals = formatArg ? parseInt(formatArg, 10) : 0
      return new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(numValue)
    }

    case 'date': {
      try {
        const dateValue = value instanceof Date ? value : new Date(String(value))
        if (isNaN(dateValue.getTime())) return String(value)

        return new Intl.DateTimeFormat(locale, getDateOptions(formatArg)).format(dateValue)
      } catch {
        return String(value)
      }
    }

    case 'datetime': {
      try {
        const dateValue = value instanceof Date ? value : new Date(String(value))
        if (isNaN(dateValue.getTime())) return String(value)

        return new Intl.DateTimeFormat(locale, getDateTimeOptions(formatArg)).format(dateValue)
      } catch {
        return String(value)
      }
    }

    case 'phone': {
      return formatUSPhone(String(value))
    }

    case 'text': {
      const strValue = String(value)
      switch (formatArg) {
        case 'upper':
          return strValue.toUpperCase()
        case 'lower':
          return strValue.toLowerCase()
        case 'title':
          return toTitleCase(strValue)
        default:
          return strValue
      }
    }

    default:
      // Unknown format - return as string
      return String(value)
  }
}

/**
 * Check if a display_format is valid.
 */
export function isValidDisplayFormat(format: string): boolean {
  const validPrefixes = ['currency', 'number', 'percent', 'date', 'datetime', 'phone', 'text']
  const formatType = format.split(':')[0]
  return validPrefixes.includes(formatType)
}
